import React, { useRef, useState } from 'react';
import { Eye, Download, X } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useIsEmpty, FILL } from './AutoFit';

const SectionWrapper = ({ title, children, id }) => {
  const sectionRef = useRef(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const empty = useIsEmpty(sectionRef);

  const handleDownload = async () => {
    if (!sectionRef.current || downloading) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(sectionRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const safeName = (title || id || 'section').replace(/[^a-zA-Z0-9]/g, '_');
      pdf.save(`${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error('PDF download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className={`relative group h-full min-w-0 ${empty ? 'hidden' : ''}`} data-testid={`section-${id}`}>
        {/* Floating action buttons */}
        <div className="absolute top-3 right-3 z-10 flex gap-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
          <button
            onClick={() => setViewOpen(true)}
            className="p-1.5 rounded-md bg-white/90 border border-gray-200 shadow-sm hover:bg-blue-50 hover:border-blue-300 transition"
            title="View fullscreen"
            data-testid={`view-btn-${id}`}
          >
            <Eye className="h-3.5 w-3.5 text-gray-600" />
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="p-1.5 rounded-md bg-white/90 border border-gray-200 shadow-sm hover:bg-green-50 hover:border-green-300 transition disabled:opacity-50"
            title="Download as PDF"
            data-testid={`download-btn-${id}`}
          >
            <Download className={`h-3.5 w-3.5 ${downloading ? 'text-gray-400 animate-pulse' : 'text-gray-600'}`} />
          </button>
        </div>
        <div ref={sectionRef} className={`h-full ${FILL}`}>
          {children}
        </div>
      </div>

      {/* Fullscreen view dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] overflow-y-auto p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-800">{title}</h2>
            <button onClick={() => setViewOpen(false)} className="p-1 rounded hover:bg-gray-100">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SectionWrapper;
